import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { FormattedMessage } from 'react-intl'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineCalendarCheckIcon from 'LuxKit/Icons/line/LineCalendarCheckIcon'
import LineUserMdIcon from 'LuxKit/Icons/line/LineUserMdIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import type { CSSProperties } from 'react'
import styled from 'styled-components'
import eases from 'styles/tools/eases'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useRebrandVersion from 'hooks/useRebrandVersion'

const SLIDE_COUNT = 3
const SLIDE_DURATION_MS = 3000
const SLIDE_GAP = 16

const CruiseUSPs = styled.div`
  --cruise-usp-carousel-direction: 1;

  display: grid;
  gap: ${rem(SLIDE_GAP)};
  overflow: visible;
  margin-top: ${rem(24)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(36)};
    grid-template-columns: unset;
    grid-auto-flow: column;
    justify-content: space-between;
  }

  &.carousel {
    grid-template-columns: repeat(${SLIDE_COUNT}, 100%);
    transition: transform 0.5s ${eases.power2.inOut};

    [dir='rtl'] & {
      --cruise-usp-carousel-direction: -1;
    }

    ${mediaQueryUp.desktop} {
      grid-template-columns: unset;
    }
  }
`

const CruiseUSP = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;

  > svg {
    align-self: center;
  }

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.desktop} {
    width: ${rem(360)};
  }
`

const AnimationContainer = styled(VerticalSpacer)`
  overflow: hidden;
  margin-inline: -${rem(16)};
  padding-inline: ${rem(16)};

  > ${mediaQueryUp.desktop} {
    margin-inline: 0;
    padding-inline: 0;
  }
`

function CruiseVerticalUSPs() {
  const isCarouselEnabled = !useScreenSizeUp('desktop')
  const iconColour = useRebrandVersion() === 'rebrand' ? 'highlight-primary-dark' : 'normal-contrast'
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(SLIDE_COUNT, SLIDE_DURATION_MS, { enabled: isCarouselEnabled })

  const slideStyle: CSSProperties | undefined = isCarouselEnabled ? {
    transform: `translateX(calc(${-activeIndex} * (100% + ${rem(SLIDE_GAP)}) * var(--cruise-usp-carousel-direction)))`,
  } : undefined

  return <AnimationContainer gap={20}>
    <CruiseUSPs className="carousel" style={slideStyle}>
      <CruiseUSP>
        <LineStarIcon size="L" colour={iconColour} />
        <VerticalSpacer gap={4}>
          <BodyText colour="normal-contrast" variant="medium" weight="bold">
            <FormattedMessage
              id="InnZ1RNu5ogxrSQwYu5a"
              defaultMessage="Limited-Time Exclusive Offers"
              description="Heading title for exclusive offers USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              id="lhpbVGVcmCODXegHAk-L"
              defaultMessage="Enjoy the best market offers with added value and exclusive inclusions like specialty dining and onboard credit."
              description="Description for exclusive offers USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineCalendarCheckIcon size="L" colour={iconColour} />
        <VerticalSpacer gap={4}>
          <BodyText colour="normal-contrast" variant="medium" weight="bold">
            <FormattedMessage
              id="D2g_2kebwDfguIYf8jfd"
              defaultMessage="Instant Online Bookings"
              description="Heading title for online bookings USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              id="dN1DOSByImUfFroEjVnt"
              defaultMessage="Browse and book thousands of itineraries and dates with ease and convenience online."
              description="Description for online bookings USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>

      <CruiseUSP>
        <LineUserMdIcon size="L" colour={iconColour} />
        <VerticalSpacer gap={4}>
          <BodyText colour="normal-contrast" variant="medium" weight="bold">
            <FormattedMessage
              id="Y3vgDHd4CaNwOxjuATe9"
              defaultMessage="Dedicated Cruise Experts"
              description="Heading title for cruise experts USP in cruise booking"
            />
          </BodyText>
          <BodyText variant="medium" colour="light-foreground-alt">
            <FormattedMessage
              id="9HKpEbgthqinQBB742sJ"
              defaultMessage="Trust our cruise experts to provide exceptional service and guidance throughout your journey."
              description="Description for cruise experts USP in cruise booking"
            />
          </BodyText>
        </VerticalSpacer>
      </CruiseUSP>
    </CruiseUSPs>

    <CSSBreakpoint max="tablet">
      <PaginationDots
        position="start"
        count={SLIDE_COUNT}
        activeIndex={activeIndex}
        slideDuration={SLIDE_DURATION_MS}
        onSelect={goToSlide}
        paused={paused}
        onHoverChange={setPaused}
      />
    </CSSBreakpoint>
  </AnimationContainer>
}

export default CruiseVerticalUSPs
